import { useContext, useState } from "react";
import CompanyService from "../../../services/CompanyService";
import CurrencyService, {
  CurrencyEdit as Currency,
  CurrencyListResponseItem
} from "../../../services/CurrencyService";
import { exponentialToDecimal } from "../../../utils/FormatUtils";
import Dropdown from "../../shared/components/Dropdown";
import {
  CheckBoxEditor,
  TextEditor,
  TextEditorNumber
} from "../../shared/components/Editors";
import { TranslationService } from "../../../services/TranslationService";
import {
  RequiredManager,
  ValidationMessage
} from "../../shared/RequieredManager";
import TooltipComponent from "../../shared/TooltipComponent";
import ToastContext from "../../shared/bootstrap/Toast";
import FloatingPanelService from "../../shared/FloatingPanel";
import ModalService from "../../shared/bootstrap/modal/Modal";
import { FloatingPanelFooter } from "../../shared/components/FloatingPanelFooter";
import { getError } from "../../../utils/RequestUtils";
import { Cast } from "../../../utils/Utils";

type CurrencyItem = CurrencyListResponseItem;

const CurrencyEdit = ({
  reload,
  current = undefined
}: {
  reload: () => void;
  current?: CurrencyItem;
}) => {
  const { translate } = TranslationService;
  const { showToast } = useContext(ToastContext);
  const [currency, setCurrency] = useState<Currency>(from(current));
  const [submiting, setSubmiting] = useState("");
  const requiredManager = new RequiredManager();
  const defaultOnChange = (fieldId: keyof Currency) => (value?: string) =>
    setCurrency((currency) => ({ ...currency, [fieldId]: value }));

  const onSubmit = async () => {
    if (submiting !== "") {
      return;
    }
    if (!requiredManager.validate()) {
      showToast(translate.MissingRequiredFields);
      return;
    }
    setSubmiting("set");
    const result = await CurrencyService.set(currency);
    if (result instanceof Error) {
      const err = getError(result);
      let errorMessage = TranslationService.translate.ErrorProcessingRequest;
      if (err.status === 400) {
        if (err.message === "Fail-DefaultAutoUpdate") {
          errorMessage = TranslationService.translate.CurrencyCannotAutoupdate;
        }
      }
      showToast(errorMessage, undefined, "danger");
      setSubmiting("");
      return;
    }
    setSubmiting("");
    showToast(translate.CurrencySaved, undefined, "success");
    FloatingPanelService.hidePanel();
    reload();
  };
  const onDelete = () => {
    if (currency.currencyId === CompanyService.getDefaultCurrencyId()) {
      ModalService.showDefaultModal({
        message: translate.CurrencyNotDeletePrincipal
      });
      return;
    }

    if (submiting !== "") {
      return;
    }
    const deleteCurrency = async () => {
      if (submiting) {
        return;
      }
      setSubmiting("del");
      const result = await CurrencyService.delete(current?.CurrencyID!);
      if (result instanceof Error) {
        const err = getError(result);
        let errorMessage = TranslationService.translate.ErrorProcessingRequest;
        if (err.status === 400) {
          if (err.message === "Fail-IO") {
            errorMessage =
              TranslationService.translate.CurrencyCannotDeleteWithIO;
          }
        }
        showToast(errorMessage, undefined, "danger");
        setSubmiting("");
        return;
      }
      showToast(translate.CurrencyDeleted, undefined, "success");
      setSubmiting("");
      FloatingPanelService.hidePanel();
      reload();
    };
    ModalService.showDefaultModal({
      acceptButtonLabel: translate.Delete,
      message: () => (
        <>
          {translate.AreYouSureDeleteCurrency.split("\n").map((x) => (
            <>
              <span>{x}</span>
              <br />
            </>
          ))}
        </>
      ),
      onAcceptClick: deleteCurrency
    });
  };
  const onNameChange = requiredManager.makeRequired(
    defaultOnChange("name")
  ) as (value?: string) => void;
  const onSymbolChange = requiredManager.makeRequired(
    defaultOnChange("symbol")
  ) as (value?: string) => void;
  const onCurrencyRateChange = requiredManager.makeRequired(
    defaultOnChange("rate")
  ) as (value?: string) => void;
  const onCurrencyAutoReferenceChange = requiredManager.makeRequired(
    defaultOnChange("autoreference")
  ) as (value?: string | null) => void;
  const onCurrencyAutoUpdateChange = requiredManager.makeRequired(
    (value: boolean) => {
      if (value) {
        if (!currency.rate) {
          onCurrencyRateChange("1");
        }
        if (!currency.autoreference) {
          onCurrencyAutoReferenceChange("AED");
        }
      } else {
        onCurrencyAutoReferenceChange(null);
      }
      defaultOnChange("autoupdate")(Cast<string>(value));
    }
  ) as (value?: boolean) => void;

  return (
    <>
      <div className="floatingBody p-4">
        <div className="card-body">
          <div className="form-group mb-3">
            <label>{translate.Name}</label>
            <TextEditor onChange={onNameChange} defaultValue={currency.name} />
            {
              <ValidationMessage
                onChange={onNameChange}
                defaultValue={currency.name}
              />
            }
          </div>

          <div className="form-group mb-3">
            <div className="row">
              <div className="col">
                <label>{translate.Symbol}</label>
                <TextEditor
                  onChange={onSymbolChange}
                  defaultValue={currency.symbol}
                />
                {
                  <ValidationMessage
                    onChange={onSymbolChange}
                    defaultValue={currency.symbol}
                  />
                }
              </div>

              <div className="col">
                <label>
                  <div className="d-flex align-items-center gap-2">
                    {translate.CodeImport}
                    <TooltipComponent title={translate.CurrencyCodeTooltip}>
                      <i className="fal fa-question-circle"></i>
                    </TooltipComponent>
                  </div>
                </label>

                <TextEditor
                  onChange={defaultOnChange("code")}
                  defaultValue={currency.code}
                />
              </div>
            </div>
          </div>
          <div className="form-group mb-3">
            <label>
              <div className="d-flex align-items-center gap-2">
                {translate.ExchangeRate}
                <TooltipComponent title={translate.CurrencyExchangeRateTooltip}>
                  <i className="fal fa-question-circle"></i>
                </TooltipComponent>
              </div>
            </label>
            <div className="row align-items-center">
              <div className="col-5">
                <TextEditorNumber
                  onChange={onCurrencyRateChange}
                  value={exponentialToDecimal(currency.rate)}
                  disabled={currency.autoupdate}
                />
                {
                  <ValidationMessage
                    onChange={onCurrencyRateChange}
                    defaultValue={currency.rate?.toString()}
                  />
                }
              </div>
              <div className="col-7">
                <CheckBoxEditor
                  onChange={(value) => onCurrencyAutoUpdateChange(value)}
                  message={translate.AutoUpdate}
                  defaultValue={currency?.autoreference !== null || false}
                />
              </div>
            </div>
          </div>
          {currency.autoupdate && (
            <div className="form-group mb-3">
              <Dropdown
                onChange={onCurrencyAutoReferenceChange}
                items={[
                  { value: "AED", text: "United Arab Emirates Dirham" },
                  { value: "AFN", text: "Afghan Afghani" },
                  { value: "ALL", text: "Albanian Lek" },
                  { value: "AMD", text: "Armenian Dram" },
                  { value: "ANG", text: "Netherlands Antillean Guilder" },
                  { value: "AOA", text: "Angolan Kwanza" },
                  { value: "ARS", text: "Argentine Peso" },
                  { value: "AUD", text: "Australian Dollar" },
                  { value: "AWG", text: "Aruban Florin" },
                  { value: "AZN", text: "Azerbaijani Manat" },
                  { value: "BAM", text: "Bosnia-Herzegovina Convertible Mark" },
                  { value: "BBD", text: "Barbadian Dollar" },
                  { value: "BDT", text: "Bangladeshi Taka" },
                  { value: "BGN", text: "Bulgarian Lev" },
                  { value: "BHD", text: "Bahraini Dinar" },
                  { value: "BIF", text: "Burundian Franc" },
                  { value: "BMD", text: "Bermudan Dollar" },
                  { value: "BND", text: "Brunei Dollar" },
                  { value: "BOB", text: "Bolivian Boliviano" },
                  { value: "BRL", text: "Brazilian Real" },
                  { value: "BSD", text: "Bahamian Dollar" },
                  { value: "BTC", text: "Bitcoin" },
                  { value: "BTN", text: "Bhutanese Ngultrum" },
                  { value: "BWP", text: "Botswanan Pula" },
                  { value: "BYN", text: "New Belarusian Ruble" },
                  { value: "BYR", text: "Belarusian Ruble" },
                  { value: "BZD", text: "Belize Dollar" },
                  { value: "CAD", text: "Canadian Dollar" },
                  { value: "CDF", text: "Congolese Franc" },
                  { value: "CHF", text: "Swiss Franc" },
                  { value: "CLF", text: "Chilean Unit of Account (UF)" },
                  { value: "CLP", text: "Chilean Peso" },
                  { value: "CNY", text: "Chinese Yuan" },
                  { value: "COP", text: "Colombian Peso" },
                  { value: "CRC", text: "Costa Rican Colón" },
                  { value: "CUC", text: "Cuban Convertible Peso" },
                  { value: "CUP", text: "Cuban Peso" },
                  { value: "CVE", text: "Cape Verdean Escudo" },
                  { value: "CZK", text: "Czech Republic Koruna" },
                  { value: "DJF", text: "Djiboutian Franc" },
                  { value: "DKK", text: "Danish Krone" },
                  { value: "DOP", text: "Dominican Peso" },
                  { value: "DZD", text: "Algerian Dinar" },
                  { value: "EGP", text: "Egyptian Pound" },
                  { value: "ERN", text: "Eritrean Nakfa" },
                  { value: "ETB", text: "Ethiopian Birr" },
                  { value: "EUR", text: "Euro" },
                  { value: "FJD", text: "Fijian Dollar" },
                  { value: "GBP", text: "British Pound Sterling" },
                  { value: "GEL", text: "Georgian Lari" },
                  { value: "GGP", text: "Guernsey Pound" },
                  { value: "GHS", text: "Ghanaian Cedi" },
                  { value: "GIP", text: "Gibraltar Pound" },
                  { value: "GMD", text: "Gambian Dalasi" },
                  { value: "GNF", text: "Guinean Franc" },
                  { value: "GTQ", text: "Guatemalan Quetzal" },
                  { value: "GYD", text: "Guyanaese Dollar" },
                  { value: "HKD", text: "Hong Kong Dollar" },
                  { value: "HNL", text: "Honduran Lempira" },
                  { value: "HRK", text: "Croatian Kuna" },
                  { value: "HTG", text: "Haitian Gourde" },
                  { value: "HUF", text: "Hungarian Forint" },
                  { value: "IDR", text: "Indonesian Rupiah" },
                  { value: "ILS", text: "Israeli New Sheqel" },
                  { value: "IMP", text: "Manx pound" },
                  { value: "INR", text: "Indian Rupee" },
                  { value: "IQD", text: "Iraqi Dinar" },
                  { value: "IRR", text: "Iranian Rial" },
                  { value: "ISK", text: "Icelandic Krna" },
                  { value: "JEP", text: "Jersey Pound" },
                  { value: "JMD", text: "Jamaican Dollar" },
                  { value: "JOD", text: "Jordanian Dinar" },
                  { value: "JPY", text: "Japanese Yen" },
                  { value: "KES", text: "Kenyan Shilling" },
                  { value: "KGS", text: "Kyrgystani Som" },
                  { value: "KHR", text: "Cambodian Riel" },
                  { value: "KMF", text: "Comorian Franc" },
                  { value: "KPW", text: "North Korean Won" },
                  { value: "KRW", text: "South Korean Won" },
                  { value: "KWD", text: "Kuwaiti Dinar" },
                  { value: "KYD", text: "Cayman Islands Dollar" },
                  { value: "KZT", text: "Kazakhstani Tenge" },
                  { value: "LAK", text: "Laotian Kip" },
                  { value: "LBP", text: "Lebanese Pound" },
                  { value: "LKR", text: "Sri Lankan Rupee" },
                  { value: "LRD", text: "Liberian Dollar" },
                  { value: "LSL", text: "Lesotho Loti" },
                  { value: "LTL", text: "Lithuanian Litas" },
                  { value: "LVL", text: "Latvian Lats" },
                  { value: "LYD", text: "Libyan Dinar" },
                  { value: "MAD", text: "Moroccan Dirham" },
                  { value: "MDL", text: "Moldovan Leu" },
                  { value: "MGA", text: "Malagasy Ariary" },
                  { value: "MKD", text: "Macedonian Denar" },
                  { value: "MMK", text: "Myanma Kyat" },
                  { value: "MNT", text: "Mongolian Tugrik" },
                  { value: "MOP", text: "Macanese Pataca" },
                  { value: "MRO", text: "Mauritanian Ouguiya" },
                  { value: "MUR", text: "Mauritian Rupee" },
                  { value: "MVR", text: "Maldivian Rufiyaa" },
                  { value: "MWK", text: "Malawian Kwacha" },
                  { value: "MXN", text: "Mexican Peso" },
                  { value: "MYR", text: "Malaysian Ringgit" },
                  { value: "MZN", text: "Mozambican Metical" },
                  { value: "NAD", text: "Namibian Dollar" },
                  { value: "NGN", text: "Nigerian Naira" },
                  { value: "NIO", text: "Nicaraguan Córdoba" },
                  { value: "NOK", text: "Norwegian Krone" },
                  { value: "NPR", text: "Nepalese Rupee" },
                  { value: "NZD", text: "New Zealand Dollar" },
                  { value: "OMR", text: "Omani Rial" },
                  { value: "PAB", text: "Panamanian Balboa" },
                  { value: "PEN", text: "Peruvian Nuevo Sol" },
                  { value: "PGK", text: "Papua New Guinean Kina" },
                  { value: "PHP", text: "Philippine Peso" },
                  { value: "PKR", text: "Pakistani Rupee" },
                  { value: "PLN", text: "Polish Zloty" },
                  { value: "PYG", text: "Paraguayan Guarani" },
                  { value: "QAR", text: "Qatari Rial" },
                  { value: "RON", text: "Romanian Leu" },
                  { value: "RSD", text: "Serbian Dinar" },
                  { value: "RUB", text: "Russian Ruble" },
                  { value: "RWF", text: "Rwandan Franc" },
                  { value: "SAR", text: "Saudi Riyal" },
                  { value: "SBD", text: "Solomon Islands Dollar" },
                  { value: "SCR", text: "Seychellois Rupee" },
                  { value: "SDG", text: "Sudanese Pound" },
                  { value: "SEK", text: "Swedish Krona" },
                  { value: "SGD", text: "Singapore Dollar" },
                  { value: "SHP", text: "Saint Helena Pound" },
                  { value: "SLL", text: "Sierra Leonean Leone" },
                  { value: "SOS", text: "Somali Shilling" },
                  { value: "SRD", text: "Surinamese Dollar" },
                  { value: "STD", text: "So Tom and Príncipe Dobra" },
                  { value: "SVC", text: "Salvadoran Colón" },
                  { value: "SYP", text: "Syrian Pound" },
                  { value: "SZL", text: "Swazi Lilangeni" },
                  { value: "THB", text: "Thai Baht" },
                  { value: "TJS", text: "Tajikistani Somoni" },
                  { value: "TMT", text: "Turkmenistani Manat" },
                  { value: "TND", text: "Tunisian Dinar" },
                  { value: "TOP", text: "Tongan Paʻanga" },
                  { value: "TRY", text: "Turkish Lira" },
                  { value: "TTD", text: "Trinidad and Tobago Dollar" },
                  { value: "TWD", text: "New Taiwan Dollar" },
                  { value: "TZS", text: "Tanzanian Shilling" },
                  { value: "UAH", text: "Ukrainian Hryvnia" },
                  { value: "UGX", text: "Ugandan Shilling" },
                  { value: "USD", text: "United States Dollar" },
                  { value: "UYU", text: "Uruguayan Peso" },
                  { value: "UZS", text: "Uzbekistan Som" },
                  { value: "VEF", text: "Venezuelan Bolívar Fuerte" },
                  { value: "VND", text: "Vietnamese Dong" },
                  { value: "VUV", text: "Vanuatu Vatu" },
                  { value: "WST", text: "Samoan Tala" },
                  { value: "XAF", text: "CFA Franc BEAC" },
                  { value: "XCD", text: "East Caribbean Dollar" },
                  { value: "XDR", text: "Special Drawing Rights" },
                  { value: "XOF", text: "CFA Franc BCEAO" },
                  { value: "XPF", text: "CFP Franc" },
                  { value: "YER", text: "Yemeni Rial" },
                  { value: "ZAR", text: "South African Rand" },
                  { value: "ZMK", text: "Zambian Kwacha (pre-2013)" },
                  { value: "ZMW", text: "Zambian Kwacha" },
                  { value: "ZWL", text: "Zimbabwean Dollar" }
                ]}
                optionLabel={TranslationService.translate.Currency}
                defaultValue={current?.AutoReference?.toString()}
              />
            </div>
          )}
          <div className="form-group mb-3">
            <div className="d-flex align-items-center gap-2">
              <CheckBoxEditor
                onChange={defaultOnChange("defaultcurrency") as () => {}}
                value={currency.defaultcurrency}
                disabled={
                  currency.currencyId === CompanyService.getDefaultCurrencyId()
                }
                message={TranslationService.translate.MainCurrency}
              />
              {currency.currencyId ===
                CompanyService.getDefaultCurrencyId() && (
                <TooltipComponent title={translate.CurrencyChangeMainTooltip}>
                  <i className="fal fa-question-circle"></i>
                </TooltipComponent>
              )}
            </div>
          </div>
          {currency.defaultcurrency &&
            currency.currencyId !== CompanyService.getDefaultCurrencyId() && (
              <div className="d-flex align-items-center">
                <i className="fa-regular fa-circle-exclamation me-3 textPrimary fa-lg"></i>
                <small className="text-black-50">
                  {translate.CurrencyMainMessage}
                </small>
              </div>
            )}
        </div>
      </div>

      <FloatingPanelFooter>
        {current && (
          <button
            type="button"
            className="btn btn-danger btn-sm"
            onClick={onDelete}
          >
            {translate.Delete}
            {submiting === "del" && (
              <i className="fas fa-spinner-third fa-spin third ms-2"></i>
            )}
          </button>
        )}

        <button className="btn btn-primary btn-sm" onClick={onSubmit}>
          {translate.Save}
          {submiting === "set" && (
            <i className="fas fa-spinner-third fa-spin third ms-2"></i>
          )}
        </button>
      </FloatingPanelFooter>
    </>
  );
};

export default CurrencyEdit;

function from(current: CurrencyItem | undefined): Currency {
  if (current) {
    return {
      code: current.Code,
      name: current.Name,
      rate: current.Rate,
      symbol: current.Symbol,
      autoupdate:
        current.AutoReference !== null &&
        current.AutoReference !== undefined &&
        current.AutoReference.length > 0,
      autoreference: current.AutoReference,
      currencyId: current.CurrencyID,
      defaultcurrency:
        current.CurrencyID === CompanyService.getDefaultCurrencyId()
    };
  }
  return {
    code: "",
    name: "",
    rate: 1,
    symbol: "",
    autoupdate: false,
    defaultcurrency: false,
    autoreference: null,
    currencyId: undefined
  };
}
